import React, { useEffect, useState, useCallback } from "react";
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, Handle, Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CP } from "./ui";

const NODE_STYLE = {
  person: { bg: "#12275A", fg: "#fff", border: "#12275A" },
  self: { bg: "#2563EB", fg: "#fff", border: "#2563EB" },
  skill: { bg: "#F3F0FF", fg: "#6D4AFF", border: "#D8CEFE" },
  task: { bg: "#FFF7E6", fg: "#B45309", border: "#FDE68A" },
};

function EgoNode({ data }) {
  const s = NODE_STYLE[data.kind] || NODE_STYLE.person;
  return (
    <div style={{
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`, borderRadius: 8,
      padding: "6px 10px", fontSize: 11, minWidth: 90, textAlign: "center",
      boxShadow: data.kind === "self" ? "0 0 0 3px rgba(37,99,235,0.25)" : "0 1px 2px rgba(15,23,42,0.08)",
    }}>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1, opacity: 0.75 }}>{data.kind}</div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{data.label}</div>
      {data.role && <div style={{ fontSize: 9, opacity: 0.75, marginTop: 2 }}>{data.role}</div>}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
}
const NODE_TYPES = { egoNode: EgoNode };

function layoutRadial(nodes, centerId) {
  const buckets = { person: [], skill: [], task: [] };
  nodes.forEach((n) => { if (n.id !== centerId) (buckets[n.type] || buckets.person).push(n); });
  const CENTER = { x: 400, y: 260 };
  const positioned = [{ ...nodes.find((n) => n.id === centerId), position: CENTER, kind: "self" }];
  const place = (arr, radius, startAngle) => {
    if (!arr.length) return;
    const step = (Math.PI * 2) / Math.max(arr.length, 6);
    arr.forEach((node, i) => {
      const a = startAngle + step * i;
      positioned.push({ ...node, position: { x: CENTER.x + Math.cos(a) * radius, y: CENTER.y + Math.sin(a) * radius } });
    });
  };
  place(buckets.skill, 180, -Math.PI / 2);
  place(buckets.person, 300, Math.PI / 3);
  place(buckets.task, 280, Math.PI);
  return positioned;
}

export default function PersonNetwork() {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({ people: 0, skills: 0, tasks: 0 });

  useEffect(() => {
    if (query.trim().length < 2) { setCandidates([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/capability/users/search", { params: { q: query.trim() } });
        setCandidates(data || []);
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadGraph = useCallback(async (user) => {
    setBusy(true);
    setSelectedUser(user);
    setCandidates([]);
    setQuery(user.name);
    try {
      const { data } = await api.get(`/capability/neighborhood/${user.user_id}`);
      const rawNodes = (data.nodes || []).map((n) => ({
        id: n.id, type: "egoNode", data: { label: n.label, role: n.role, kind: n.type }, position: { x: 0, y: 0 },
      }));
      setNodes(layoutRadial(rawNodes, data.center));
      setEdges((data.edges || []).map((e, i) => ({
        id: `e-${i}-${e.source}-${e.target}`, source: e.source, target: e.target, label: e.type,
        labelStyle: { fontSize: 9, fill: CP.sub, textTransform: "uppercase", letterSpacing: 1 },
        style: {
          stroke: e.type === "COLLABORATED_WITH" ? CP.blue : e.type === "HAS_SKILL" ? "#D8CEFE" :
            e.type === "CONTRIBUTED_TO" ? "#FDE68A" : "#94A3B8",
          strokeWidth: 1.5,
        },
        animated: e.type === "COLLABORATED_WITH",
      })));
      setStats({
        people: (data.nodes || []).filter((n) => n.type === "person").length,
        skills: (data.nodes || []).filter((n) => n.type === "skill").length,
        tasks: (data.nodes || []).filter((n) => n.type === "task").length,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }, [setNodes, setEdges]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data?.user_id) loadGraph({ user_id: data.user_id, name: data.name });
      } catch { /* silent */ }
    })();
  }, [loadGraph]);

  const onNodeClick = useCallback((_evt, node) => {
    if (node?.data?.kind === "person") loadGraph({ user_id: node.id, name: node.data.label });
  }, [loadGraph]);

  return (
    <div className="space-y-4" data-testid="network-graph">
      <div className="rounded-xl bg-white shadow-sm p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end relative" style={{ border: `1px solid ${CP.border}` }}>
        <div className="md:col-span-9 relative">
          <div className="text-xs font-semibold mb-1" style={{ color: CP.sub }}>Find a person</div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Type at least 2 characters…"
            className="w-full h-10 px-3 rounded-lg text-sm bg-white outline-none"
            style={{ border: `1px solid ${CP.border}`, color: CP.text }} data-testid="network-user-search" />
          {candidates.length > 0 && (
            <div className="absolute z-20 top-full left-0 right-0 mt-1 rounded-lg bg-white max-h-64 overflow-auto shadow-lg" style={{ border: `1px solid ${CP.border}` }} data-testid="network-user-results">
              {candidates.map((c) => (
                <button key={c.user_id} className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  onClick={() => loadGraph(c)} data-testid={`network-user-option-${c.user_id}`}>
                  <div className="text-sm font-medium" style={{ color: CP.text }}>{c.name}</div>
                  <div className="text-[10px]" style={{ color: CP.sub }}>{c.title || c.role} · {c.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-3 grid grid-cols-3 gap-2 text-center">
          {[["People", stats.people], ["Skills", stats.skills], ["Tasks", stats.tasks]].map(([l, v]) => (
            <div key={l} className="rounded-lg p-2" style={{ border: `1px solid ${CP.border}` }}>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: CP.sub }}>{l}</div>
              <div className="font-bold text-lg" style={{ color: CP.navy }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm overflow-hidden" style={{ border: `1px solid ${CP.border}`, height: 540 }} data-testid="network-graph-canvas">
        {selectedUser ? (
          <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick} nodeTypes={NODE_TYPES} fitView fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}>
            <Background gap={16} color="#E8EDF7" />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeStrokeWidth={2} nodeColor={(n) => NODE_STYLE[n.data?.kind]?.bg || "#999"} />
          </ReactFlow>
        ) : (
          <div className="h-full flex items-center justify-center text-sm" style={{ color: CP.sub }}>
            {busy ? "Loading network…" : "Search for a person to render their capability network."}
          </div>
        )}
      </div>
    </div>
  );
}
